import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { TagGroup } from '../../../hooks/useTagGroups';
import downArrow from '../../../assets/images/down-arrow.svg';

export const TagGroupMultiSelect: React.FC<{
  label?: string;
  tagGroups: TagGroup[];
  selectedIds: number[];
  onChange: (ids: number[]) => void;
  placeholder?: string;
  isLoading?: boolean;
}> = ({ label, tagGroups, selectedIds, onChange, placeholder = 'Select topics', isLoading }) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const selected = useMemo(() => {
    const set = new Set(selectedIds);
    return tagGroups.filter((g) => set.has(g.id));
  }, [tagGroups, selectedIds]);

  const buttonLabel = useMemo(() => {
    if (isLoading) return 'Loading...';
    if (!selected.length) return placeholder;
    if (selected.length <= 2) return selected.map((g) => g.name).join(', ');
    return `${selected[0].name}, ${selected[1].name} +${selected.length - 2} more`;
  }, [isLoading, selected, placeholder]);

  const handleToggle = (id: number) => {
    if (selectedIds.includes(id)) {
      onChange(selectedIds.filter((i) => i !== id));
    } else {
      onChange([...selectedIds, id]);
    }
  };

  return (
    <div className="flex flex-col gap-1" ref={containerRef}>
      {label && <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</label>}
      <div className="relative">
        <button
          type="button"
          className="focus:ring-blue-primary flex w-full items-center justify-between rounded-md border border-gray-300 px-3 py-2 text-left text-sm text-gray-700 focus:outline-none focus:ring-2 disabled:cursor-not-allowed disabled:opacity-50"
          onClick={() => setIsOpen((p) => !p)}
          disabled={isLoading}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
        >
          <span className="truncate">{buttonLabel}</span>
          <img src={downArrow} alt="" className={`ml-2 h-2 w-2 transform transition ${isOpen ? '' : 'rotate-180'}`} />
        </button>
        {isOpen && (
          <div className="absolute z-10 mt-1 w-full overflow-hidden rounded-md border border-gray-200 bg-white shadow-lg">
            <div className="max-h-60 overflow-y-auto">
              {tagGroups.length ? (
                tagGroups.map((g) => (
                  <label
                    key={g.id}
                    className="flex cursor-pointer items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    <input
                      type="checkbox"
                      className="text-blue-primary focus:ring-blue-primary h-4 w-4 rounded border-gray-300"
                      checked={selectedIds.includes(g.id)}
                      onChange={() => handleToggle(g.id)}
                    />
                    {g.name}
                  </label>
                ))
              ) : (
                <div className="px-3 py-2 text-sm text-gray-500">No topics available.</div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
